import {
  Dialog,
  DomElementAlignment,
  DomTargetPosition,
  fieldValidationErrorMessage,
  IconButton,
  isDropDownNoSelection,
  isDropDownResult,
  OdinIconSize,
  Tooltip,
  useScreenSize,
  useTestTag,
} from '@myosh/odin-components';
import type {
  DataGridRef,
  DropDownNoSelection,
  DropDownResult,
  JsonData,
  ModalDialogButtonSetting,
} from '@myosh/odin-components';
import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import useViewConfigs from '../../hooks/use-view-configs';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Subject, takeUntil } from 'rxjs';
import * as yup from 'yup';
import type { FormPermissionsResults } from '../../@types/form-permissions';
import type { Module, ModuleForm } from '../../@types/modules';
import type { ViewConfig } from '../../@types/views';
import useActiveRecordRef from '../../hooks/use-active-record-ref';
import { useFormSettingsQuery } from '../../redux/services/api';
import { fileApi } from '../../redux/services/file';
import { recordApi } from '../../redux/services/record';
import { userApi } from '../../redux/services/user';
import { useUpdateViewConfigMutation } from '../../redux/services/view';
import {
  dynamicPageState,
  setSelectedAuthorFilter,
  setSelectedForm,
  setSelectedLastEditedFilter,
  setSelectedRecordsFilter,
  setSelectedViewConfig,
} from '../../redux/slices/dynamic-page-slice';
import { getDataGridReference } from '../../services/data-grid.service';
import { dataTag } from '../../utils/data-tags';
import {
  defaultRecordAuthorFilterOption,
  resolveLastEditedFilterConfigValue,
  resolveRecordFilterConfigValue,
} from '../../utils/dynamic-page-utils';
import { forceAssert } from '../../utils/type-guards';
import PrimaryViewActionButton from '../common/primary-view-action-button';
import HfDropDown from '../fields/custom-drop-downs/hf-drop-down.component';
import OdinDropDown from '../fields/custom-drop-downs/odin-drop-down.component';
import { RecordTitle } from '../record/record-title.component';
import { RecordSaveState } from '../record/record.component';
import { DynamicPageHeaderFilters } from './dynamic-page-header-filters';
import GlobalHierarchiesFilter from './global-hierarchies-filter.component';
import DataGridMenu from './menu/data-grid-menu';

interface DynamicPageHeaderProps {
  selectedForm: DropDownResult;
  module: Module;
  forms: ModuleForm[];
  viewHasChanged?: boolean;
  onFormChange: (form: DropDownResult) => void;
  permissions?: { create?: boolean; showCreateButton?: boolean };
  formPermissions?: FormPermissionsResults;
  allFormsExportPermission?: boolean;
  listOfFormIdsWithCreatePermission?: number[];
  onToggleCalendarView?: () => void;
  isCalendarView?: boolean;
}

function DynamicPageHeader({
  selectedForm,
  module,
  forms,
  viewHasChanged,
  onFormChange,
  permissions,
  formPermissions,
  allFormsExportPermission,
  listOfFormIdsWithCreatePermission,
  onToggleCalendarView,
  isCalendarView,
}: DynamicPageHeaderProps) {
  const [isSelectFormModalVisible, setIsSelectFormModalVisible] = useState(false);

  const gridRef = useRef<DataGridRef | undefined>(undefined);
  const destroySubject = useRef<Subject<void> | undefined>(undefined);

  const { selectedFilters } = useAppSelector(dynamicPageState);
  const isAllForms = selectedForm.value === 0;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { activeRecordReference } = useActiveRecordRef();
  const { isMobile } = useScreenSize();

  const [updateLastUsedView] = useUpdateViewConfigMutation();

  const { data: viewConfigs, lastUsed: lastUsedView } = useViewConfigs(
    selectedForm?.value as number,
    true,
    module.id,
    isAllForms
  );

  // Get form settings to check if calendar view is enabled
  const { data: formSettings } = useFormSettingsQuery(selectedForm?.value as number, {
    skip: isAllForms || !selectedForm?.value || selectedForm.value === 0,
  });

  useEffect(() => {
    destroySubject.current = new Subject<void>();
    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((dataGridRef) => {
        if (dataGridRef) {
          gridRef.current = dataGridRef;
        }
      });

    return () => {
      destroySubject.current?.next();
      destroySubject.current?.complete();
    };
  }, []);

  const onViewConfigChange = useCallback(
    (config?: DropDownResult | Array<DropDownResult> | DropDownNoSelection, updateView = true) => {
      const debouncedFunction = debounce(() => {
        if (config && !isDropDownNoSelection(config) && !Array.isArray(config)) {
          dispatch(setSelectedViewConfig(isDropDownResult(config) ? config : undefined));

          // set the saved/default value for the view filters
          const viewConfig = viewConfigs?.find((item: ViewConfig) => item.id === config.value);

          const selectedRecordsFilter = resolveRecordFilterConfigValue(viewConfig?.archivedType);
          dispatch(setSelectedRecordsFilter(selectedRecordsFilter));

          if (!isAllForms) {
            const selectedLastEditedFilter = resolveLastEditedFilterConfigValue(viewConfig?.dateRange);
            dispatch(setSelectedLastEditedFilter(selectedLastEditedFilter));
            dispatch(setSelectedAuthorFilter(defaultRecordAuthorFilterOption));
          }

          if (updateView && viewConfig) {
            updateLastUsedView({ id: viewConfig.id, lastUsed: new Date().toISOString(), skipTagInValidation: true });
          }
        }
      }, 5);

      debouncedFunction();
    },
    [dispatch, viewConfigs, isAllForms, updateLastUsedView]
  );

  useEffect(() => {
    if (lastUsedView) {
      onViewConfigChange({ value: lastUsedView.id, text: lastUsedView.name }, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUsedView]);

  const tagCreator = useTestTag('dynamic-page-header');

  const updateSelectedForm = (selectedForm: DropDownResult) => {
    dispatch(setSelectedForm({ viewId: module.viewId, form: selectedForm }));
  };

  const onSelectedFormChange = debounce((value?: DropDownResult | Array<DropDownResult> | DropDownNoSelection) => {
    if (value && !isDropDownNoSelection(value)) {
      if (!Array.isArray(value)) {
        onFormChange(value);
        updateSelectedForm(value);
      } else {
        updateSelectedForm(value[0]);
      }
    }
  }, 5);

  const openNewRecord = (selectedForm: DropDownResult) => {
    const panelCount = activeRecordReference.current?.currentRecordCount() ?? 0;
    const title = t('form-new-record', { formName: selectedForm.text });
    const details = <RecordTitle title={title} />;

    activeRecordReference.current?.addActiveRecord(`new_${panelCount + 1}`, module.icon, details, false, {
      title,
      recordState: RecordSaveState.New,
      viewId: module.viewId,
      formId: selectedForm.value,
      formName: selectedForm.text,
      moduleIcon: module.icon,
    });
  };

  const onAddRecordClick = () => {
    if (isAllForms) {
      setIsSelectFormModalVisible(true);
    } else if (selectedForm && !isDropDownNoSelection(selectedForm)) {
      openNewRecord(selectedForm);
    }
  };

  const hideSelectFormModal = () => {
    reset();
    setIsSelectFormModalVisible(false);
  };

  const onFormModalSubmit = (data: FieldValues) => {
    openNewRecord(data['form']);
    hideSelectFormModal();
  };
  const onError = (data: FieldValues) => console.error(data);

  const selectFormModalButtons: Array<ModalDialogButtonSetting> = [
    {
      name: 'select',
      text: t('select'),
      form: 'selectFormModal',
      type: 'primary',
    },
  ];

  const schema = yup.object().shape({
    form: yup.object().required(fieldValidationErrorMessage(t('form'))),
  });

  const { control, handleSubmit, reset } = useForm<FieldValues>({
    resolver: yupResolver(schema) as never,
    mode: 'onSubmit',
  });

  const headerContainerCx = 'flex flex-col flex-shrink-0 items-start pr-2 transition-transform';

  const refreshDataGrid = () => {
    gridRef?.current?.api.data.getDataOverwritePageCache();
    dispatch(
      recordApi.util.invalidateTags([
        'Record',
        'LinkedRecord',
        'ReverseLinkedRecord',
        'SummaryLinkedRecord',
        'TotalRecordCount',
        'RecordVersion',
        'Collaborator',
        'AssignedRecordsCount',
        'AllFormsRecord',
      ])
    );
    dispatch(fileApi.util.invalidateTags(['File']));
    dispatch(userApi.util.invalidateTags(['LinkedUser']));
  };

  return (
    <>
      <div className={headerContainerCx}>
        <div
          className="grid w-full grid-cols-[1fr_auto] items-center gap-4 pb-4 pr-4 pt-2 md:grid-cols-[18rem_max-content_1fr]"
          ref={tagCreator(dataTag(selectedForm.text))}
        >
          <div className="col-span-full row-start-1 md:col-span-1">
            <OdinDropDown
              textField="caption"
              valueField="id"
              data={forceAssert<JsonData>(forms)}
              allowSearch={true}
              allowClear={false}
              className="rounded-t"
              size="xlg"
              onChange={onSelectedFormChange}
              value={selectedForm}
              useInputStyle={true}
            />
          </div>
          {permissions?.create && permissions?.showCreateButton && (
            <PrimaryViewActionButton classNames="row-start-2 md:row-start-1 md:col-start-2" onClick={onAddRecordClick}>
              {t('add-record')}
            </PrimaryViewActionButton>
          )}
          <div className="row-start-2 flex items-center gap-4 md:col-span-full">
            <GlobalHierarchiesFilter className="md:hidden" />
            <DynamicPageHeaderFilters
              isAllForms={isAllForms}
              viewConfigs={viewConfigs}
              selectedFilters={selectedFilters}
              onViewConfigChange={onViewConfigChange}
            />
            {!isMobile && (
              <Tooltip
                description={t('refresh')}
                tooltipClassName="bg-mono-1"
                debounceTime={200}
                wrapperClassName="flex justify-center"
                tooltipAlignment={DomElementAlignment.BottomLeft}
                tooltipTargetPosition={DomTargetPosition.TopLeft}
              >
                <IconButton icon="RefreshCw" iconSize={OdinIconSize.Small} onClick={refreshDataGrid} />
              </Tooltip>
            )}
            {!isMobile && !isAllForms && formSettings?.enableCalendarView && (
              <Tooltip
                description={isCalendarView ? t('table-view') : t('calendar-view')}
                tooltipClassName="bg-mono-1"
                debounceTime={200}
                wrapperClassName="flex justify-center"
                tooltipAlignment={DomElementAlignment.BottomLeft}
                tooltipTargetPosition={DomTargetPosition.TopLeft}
              >
                <IconButton
                  icon={isCalendarView ? 'Table' : 'Calendar'}
                  iconSize={OdinIconSize.Small}
                  onClick={onToggleCalendarView}
                />
              </Tooltip>
            )}
            <DataGridMenu
              selectedForm={selectedForm}
              formPermissions={formPermissions}
              module={module}
              isAllForms={isAllForms}
              viewHasChanged={viewHasChanged ?? false}
              isMobile={isMobile}
              allFormsExportPermission={allFormsExportPermission}
              isCalendarView={isCalendarView}
            />
          </div>
        </div>
      </div>
      {isSelectFormModalVisible && (
        <Dialog
          header={t('select-form')}
          visible={isSelectFormModalVisible}
          onClose={hideSelectFormModal}
          buttons={selectFormModalButtons}
          buttonType="submit"
          containerClassName="w-60 sm:w-[300px]"
        >
          <form id="selectFormModal" onSubmit={handleSubmit(onFormModalSubmit, onError)}>
            <HfDropDown
              control={control}
              name="form"
              textField="caption"
              valueField="id"
              data={forceAssert<JsonData>(
                forms.filter((form) => form.id !== 0 && listOfFormIdsWithCreatePermission?.includes(form.id))
              )}
              allowClear={false}
              allowSearch={true}
              setDefaultValue={true}
            />
          </form>
        </Dialog>
      )}
    </>
  );
}

export default DynamicPageHeader;
